import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Box, Card, CardContent, CardHeader, Container, Grid, LinearProgress, Typography } from "@mui/material";

import Error404 from "../../../Errors/Error404";
import Error500 from "../../../Errors/Error500";

import BreadcrumbBuilder from "../BreadcrumbBuilder";
import ConfirmDialog from "../../../Common/Dialog/ConfirmDialog";
import ListItemMenu from "../../../Common/ListItemMenu";
import NoteForm from "./NoteForm";
import PORTAvatar from "../PORTAvatar";
import RenderDate from "../../../Common/Date/RenderDate";

import { ActionTypes } from "../../../../constants";
import { getNote as getNoteReducer, getError, getRmsAppCode } from "../../../../reducers";
import { getNoteDetails, updateNote, deleteNote } from "../../../../actions/rmsActions";


function Note({ note, onUpdate, onDelete }) {
    const history = useHistory();
    const [openDialog, setOpenDialog] = useState(null);

    const handleCloseDialog = () => setOpenDialog(null);

    const title = note?.title ?? "Note";

    return (
        <Container className="content">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <BreadcrumbBuilder />
                </Grid>

                <Grid item xs={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography={true}
                            avatar={<PORTAvatar type={"note"} />}
                            action={
                                <ListItemMenu
                                    onEdit={note?.canUpdate && (() => setOpenDialog("update"))}
                                    onDelete={note?.canDelete && (() => setOpenDialog("delete"))}
                                />
                            }
                            title={
                                <Typography component="h2" className="card-header-title" >
                                    {title}
                                </Typography>
                            }
                            subheader={
                                <Typography component="span" className="card-header-subtitle" >
                                    <RenderDate date={note.createdAt} />
                                </Typography>
                            }
                        />
                        <CardContent>
                            <Box className="card-content">
                                <Typography variant="body1">
                                    {note.description}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {openDialog === "delete" && (
                <ConfirmDialog
                    open={true}
                    title="Delete note"
                    content={`The following note will be deleted: ${note.title}`}
                    confirmButtonLabel="Delete"
                    onConfirm={() => {
                        onDelete(note.id);
                        history.replace("/portfolio/notes");
                    }}
                    onCancel={handleCloseDialog}
                    onClose={handleCloseDialog}
                />
            )}

            {openDialog === "update" && (
                <NoteForm
                    open={true}
                    onClose={handleCloseDialog}
                    scroll="paper"
                    note={note}
                />
            )}
        </Container>
    );
}

const NoteContainer = ({ match }) => {
    const dispatch = useDispatch();
    const note = useSelector((state) => getNoteReducer(state, Number(match.params.id)));
    const error = useSelector((state) => getError(state, ActionTypes.GET_NOTE_SUCCESS));
    const appCode = useSelector(getRmsAppCode);

    useEffect(() => {
        if (!!appCode) {
            const {id} = match.params;
            dispatch(getNoteDetails(id, appCode));
        }
    }, [dispatch, match.params, appCode]);

    if (!!error) {
        const { errorCode } = error;
        if (errorCode === 404) {
            return <Error404 />;
        }
        if (errorCode === 500) {
            return <Error500 />;
        }
    }

    if (!note) {
        return <LinearProgress />;
    }

    const handleUpdateNote = (id, params) => dispatch(updateNote(appCode, id, params));
    const handleDeleteNote = (id) => dispatch(deleteNote(id, appCode));

    return (
        <Note
            note={note}
            onUpdate={handleUpdateNote}
            onDelete={handleDeleteNote}
        />
    );
};

export default NoteContainer;
